import { OrdersSection } from "src/apps/components/ordersSection/OrdersSection";
import { IdentityVerificationProvider } from "src/apps/providers/IdentityVerificationProvider";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { styled, useTheme } from "styled-components";
import { TextBold } from "src/apps/components/typography/Texts";
import { OrderIdentityVerification } from "src/contexts/documentationArea/domain/OrderIdentityVerification";

import { IDVStatefulOrdersSectionHeader } from "src/apps/components/ordersSection/IDVStatefulOrdersSectionHeader";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { T } from "src/apps/components/i18n/Translation";
import { IdentityVerificationOptions } from "./IdentityVerificationOptions";
import { MobileSectionHeader } from "src/apps/components/ordersSection/MobileSectionHeader";
import { colors } from "@permisso/permisso-components";

const ACCORDION_HEADER_IDENTITY_VERIFICATIONS_HEADER =
  "documentation_area.identity_verifications.section_header";
const ACCORDION_HEADER_IDENTITY_VERIFICATIONS_DESCRIPTION =
  "documentation_area.identity_verifications.section_description";
const ACCORDION_HEADER_IDENTITY_VERIFICATIONS_DESCRIPTION_MOBILE =
  "documentation_area.identity_verifications.section_description.mobile";

const ACCORDION_HEADER_IDENTITY_VERIFICATION =
  "documentation_area.uploader_box.identity_verification";

interface IdentityVerificationsProps {
  identityVerifications: OrderIdentityVerification[];
}

export const IdentityVerifications = ({ identityVerifications }: IdentityVerificationsProps) => {
  const { isMobile } = useIsMedia();
  const { successColor, errorColor } = useTheme();

  const computeIconBasedOnResponses = (identityVerification: OrderIdentityVerification) => {
    return (
      <>
        {identityVerification.hasValidResponses() ? (
          <Icon path={ICON_PATHS.CHECK_CIRCLE_THIN} size={ICON_SIZES.s20} color={successColor} />
        ) : !identityVerification.hasValidResponses() && identityVerification.hasResponses() ? (
          <Icon path={ICON_PATHS.CROSS_CIRCLE_THIN} size={ICON_SIZES.s20} color={errorColor} />
        ) : null}
      </>
    );
  };

  if (!identityVerifications.length) return null;

  const identityVerification = identityVerifications[0];
  const isIdentityVerificationCompleted = identityVerification.hasValidResponses();

  if (isMobile) {
    return (
      <IdentityVerificationProvider>
        <MobileContainer>
          <MobileSectionHeader
            title={ACCORDION_HEADER_IDENTITY_VERIFICATIONS_HEADER}
            description={ACCORDION_HEADER_IDENTITY_VERIFICATIONS_DESCRIPTION_MOBILE}
          />
          {!isIdentityVerificationCompleted ? (
            <IdentityVerificationOptions order={identityVerification} />
          ) : (
            <>
              <MobileBox $isCompleted={isIdentityVerificationCompleted}>
                <MobileBoxHeader>
                  {computeIconBasedOnResponses(identityVerification)}
                  <TextBold>
                    <T k={ACCORDION_HEADER_IDENTITY_VERIFICATION} />
                  </TextBold>
                </MobileBoxHeader>
              </MobileBox>
            </>
          )}
        </MobileContainer>
      </IdentityVerificationProvider>
    );
  }

  return (
    <IdentityVerificationProvider>
      <OrdersSection
        disabled={isIdentityVerificationCompleted}
        header={
          <IDVStatefulOrdersSectionHeader
            title={ACCORDION_HEADER_IDENTITY_VERIFICATIONS_HEADER}
            description={ACCORDION_HEADER_IDENTITY_VERIFICATIONS_DESCRIPTION}
            order={identityVerification}
          />
        }
        content={<IdentityVerificationOptions order={identityVerification} />}
        isCompleted={isIdentityVerificationCompleted}
        collapseOnComplete={true}
      />
    </IdentityVerificationProvider>
  );
};

const MobileBox = styled.div<{ $isCompleted: boolean }>`
  border-radius: 0.5rem;
  border: ${({ $isCompleted, theme }) =>
    $isCompleted ? `0.0625rem solid ${theme.successColor}` : "none"};
  margin-top: 1rem;
  padding: 1rem;
`;

const MobileBoxHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const MobileContainer = styled.div`
  padding: 0rem 1rem 1rem 1rem;
  border-bottom: 0.0625rem solid ${colors.grey400};
`;
